import clsx from 'clsx';
import React, { FunctionComponent } from 'react';
import { PulseLoader } from 'react-spinners';
import { faBan } from '@fortawesome/pro-light-svg-icons';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons/faEllipsis';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { hasOutstandingBalance } from 'components/Client/clientOverviewUtils';
import styles from './ClientOverviewInvoicesTable.module.scss';

export interface ClientOverviewInvoice {
    id: number;
    number: number;
    date: string;
    total: string;
    paid: string;
    remaining: string;
    canVoid: boolean;
}

interface ClientOverviewInvoicesTableProps {
    invoices: ClientOverviewInvoice[];
    onOpenInvoice?: (invoiceId: number) => void;
    onCheckout?: (invoiceId: number) => void;
    onVoid?: (invoiceId: number) => void;
    checkoutLoadingInvoiceId?: number | null;
}

export const ClientOverviewInvoicesTable: FunctionComponent<ClientOverviewInvoicesTableProps> = props => {
    const isCheckoutDisabled = props.checkoutLoadingInvoiceId != null;

    return (
        <table className={styles.table}>
            <thead>
                <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Action</th>
                    <th />
                </tr>
            </thead>
            <tbody>
                {props.invoices.map(invoice => (
                    <tr key={invoice.id}>
                        <td>
                            <button
                                type="button"
                                className={styles.invoiceId}
                                onClick={() => props.onOpenInvoice?.(invoice.id)}
                            >
                                {invoice.number}
                            </button>
                        </td>
                        <td>{invoice.date}</td>
                        <td>{invoice.total}</td>
                        <td>{invoice.paid}</td>
                        <td>{invoice.remaining}</td>
                        <td>
                            {hasOutstandingBalance(invoice.remaining) && (
                                <button
                                    type="button"
                                    className={clsx(styles.checkoutButton, {
                                        [styles.checkoutButtonLoading]: props.checkoutLoadingInvoiceId === invoice.id
                                    })}
                                    disabled={isCheckoutDisabled}
                                    onClick={() => props.onCheckout?.(invoice.id)}
                                >
                                    Checkout
                                    {props.checkoutLoadingInvoiceId === invoice.id && (
                                        <span className={styles.checkoutButtonSpinner}>
                                            <PulseLoader
                                                color="#ffffff"
                                                loading
                                                size={5}
                                            />
                                        </span>
                                    )}
                                </button>
                            )}
                        </td>
                        <td className={styles.actionsCell}>
                            {invoice.canVoid && (
                                <Dropdown>
                                    <DropdownTrigger
                                        className={styles.actionsTrigger}
                                        disabled={isCheckoutDisabled}
                                    >
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </DropdownTrigger>
                                    <DropdownContent
                                        align="end"
                                        sideOffset={4}
                                        minWidth={150}
                                        zIndex={1300}
                                        showBottomModalOnMobile={false}
                                    >
                                        <DropdownItem
                                            leftIcon={faBan}
                                            className={styles.destructiveAction}
                                            onSelect={() => props.onVoid?.(invoice.id)}
                                        >
                                            Void
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default ClientOverviewInvoicesTable;
